// src/components/MobileLayout.js
import React from 'react';
import { Paper, BottomNavigation, BottomNavigationAction, Box } from '@mui/material';
import { Home, Person, Inventory2, ReceiptLong, LocalShipping, Favorite } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MobileHeader from './MobileHeader';
import FourSquaresIcon from './FourSquaresIcon';
import { FaFacebookMessenger } from 'react-icons/fa';

// Wrapper component to make FaFacebookMessenger work with Material-UI
const MessengerIcon = () => (
  <Box
    component={FaFacebookMessenger}
    sx={{
      fontSize: '24px', // Match Material-UI icon size
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  />
);

const MobileLayout = ({ children }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Enhanced Messenger click handler (same as in Product.js)
  const handleMessengerClick = () => {
    const pageId = '110699824870937';

    // Check if user is on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      // For mobile devices, try the app-specific URL first
      const messengerAppUrl = `fb-messenger://user-thread/${pageId}`;
      const messengerWebUrl = `https://m.me/${pageId}`;

      // Create a temporary link to try opening the app
      const appLink = document.createElement('a');
      appLink.href = messengerAppUrl;
      appLink.style.display = 'none';
      document.body.appendChild(appLink);

      try {
        appLink.click();

        // Set a timeout to open web version if app doesn't respond
        setTimeout(() => {
          window.open(messengerWebUrl, '_blank', 'noopener,noreferrer');
        }, 1500);
      } catch (error) {
        // If app link fails, open web version immediately
        window.open(messengerWebUrl, '_blank', 'noopener,noreferrer');
      } finally {
        document.body.removeChild(appLink);
      }
    } else {
      // For desktop, use the standard m.me URL
      window.open(`https://m.me/${pageId}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Determine active tab based on route - UPDATED VERSION
  const getActiveTab = () => {
    const path = location.pathname;

    if (isAdmin) {
      // Admin navigation order: Home, Shipping, Profile, Products, Orders
      switch(path) {
        case '/':
          return 0;
        case '/admin/shipping':
          return 1;
        case '/profile':
          return 2;
        case '/admin/products':
          return 3;
        case '/admin/orders':
          return 4;
        default:
          // Handle admin sub-routes
          if (path.startsWith('/admin/shipping')) return 1;
          if (path.startsWith('/admin/products')) return 3;
          if (path.startsWith('/admin/orders')) return 4;
          // For department routes, show categories as active
          if (path.startsWith('/department/')) return 0;
          return 0; // Default to home
      }
    } else {
      // Non-admin navigation order: Home, Categories, Profile, Favorites, Messenger
      switch(path) {
        case '/categories':
          return 1;
        case '/profile':
          return 2;
        case '/favorites':
          return 3;
        default:
          // Handle non-admin sub-routes
          if (path.startsWith('/categories')) return 1;
          if (path.startsWith('/favorites')) return 3;
          // For department routes, show home as active
          if (path.startsWith('/department/')) return 0;
          return 0; // Default to home
      }
    }
  };

  const handleChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        // Navigate to default department instead of just '/'
        navigate('/department/children');
        break;
      case 1:
        // Different navigation for admin vs non-admin
        if (isAdmin) {
          navigate('/admin/shipping');
        } else {
          // Navigate to categories page
          navigate('/categories');
        }
        break;
      case 2:
        if (isAdmin) {
          // Admin: Profile is at index 2
          navigate('/profile');
        } else {
          // Non-admin: Profile is at index 2
          navigate('/profile');
        }
        break;
      case 3:
        if (isAdmin) {
          // Admin: Products is at index 3
          navigate('/admin/products');
        } else {
          // Non-admin: Favorites is at index 3
          navigate('/favorites');
        }
        break;
      case 4:
        if (isAdmin) {
          // Admin: Orders is at index 4
          navigate('/admin/orders');
        } else {
          // Non-admin: Messenger is at index 4
          handleMessengerClick();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ direction: 'rtl', minHeight: '100vh', paddingBottom: '56px' }}>
      {/* Mobile Header with high z-index */}
      <Box sx={{ position: 'relative', zIndex: 1000 }}>
        <MobileHeader />
      </Box>
      
      {/* Main content with lower z-index */}
      <main style={{ position: 'relative', zIndex: 1 }}>{children}</main>

      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1001, // Higher than header to ensure bottom nav is always visible
        }}
        elevation={3}
      >
        <BottomNavigation value={getActiveTab()} onChange={handleChange} showLabels>
          {/* Index 0: Home - same for both admin and non-admin */}
          <BottomNavigationAction label="الرئيسية" icon={<Home />} />

          {/* Index 1: Conditionally show shipping or categories */}
          {isAdmin ? (
            <BottomNavigationAction label="الشحن" icon={<LocalShipping />} />
          ) : (
            <BottomNavigationAction label="الفئات" icon={<FourSquaresIcon />} />
          )}

          {/* Index 2: For admin = Profile, For non-admin = Profile */}
          <BottomNavigationAction label="حسابي" icon={<Person />} /> 

          {/* Index 3: For admin = Products, For non-admin = Favorites */}
          {isAdmin ? (
            <BottomNavigationAction
              label="المنتجات"
              icon={<Inventory2 />}
            />
          ) : (
            <BottomNavigationAction label="المفضلة" icon={<Favorite />} />
          )}

          {/* Index 4: For admin = Orders, For non-admin = Messenger */}
          {isAdmin ? (
            <BottomNavigationAction
              label="الطلبات"
              icon={<ReceiptLong />}
            />
          ) : (
            <BottomNavigationAction 
              label="راسلنا" 
              icon={<MessengerIcon />}
            />
          )}
        </BottomNavigation>
      </Paper>
    </div>
  );
};

export default MobileLayout;
